import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import {
  ActionService,
  BadgeButtonComponent,
  BadgeItemType,
  Icon,
  IconComponent,
  icons,
  Maybe,
  Menu,
  MenuItemType,
  OptionalAsyncPipe,
  TooltipHoverDirective,
  TooltipVisibility,
} from '@evc/web-components';

import type { Organization } from '../../../clients/core/organizations/organizations.type';
import { CoreClientUtilService } from '../../../clients/core/utils/core-client-utils.service';
import { SearchService } from '../../search/search.service';
import { SECONDARY_NAVIGATION_HOSTS, type SecondaryNavigationHosts } from '../types';

@Component({
  selector: 'evc-navigation-secondary',
  imports: [
    IconComponent,
    BadgeButtonComponent,
    OptionalAsyncPipe,
    TooltipHoverDirective,
  ],
  templateUrl: './navigation-secondary.html',
  styleUrl: './navigation-secondary.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-host]': 'host()',
  },
})
export class NavigationSecondaryComponent {
  readonly menus = input<Menu[]>();
  readonly collapsed = input<boolean>(false);
  readonly host = input<SecondaryNavigationHosts>(SECONDARY_NAVIGATION_HOSTS.LEFTBAR);
  readonly isLeftbarHost = computed(() => this.host() === SECONDARY_NAVIGATION_HOSTS.LEFTBAR);

  readonly isCollapsed = computed(() => {
    const host = this.host();

    return host === SECONDARY_NAVIGATION_HOSTS.LEFTBAR && this.collapsed();
  });

  #actionService = inject(ActionService);
  #destroyRef = inject(DestroyRef);
  #searchService = inject(SearchService, { optional: true });
  #coreClientUtilService = inject(CoreClientUtilService);

  protected readonly menusLocal = signal<Menu[]>([]);
  #syncMenusLocal = effect(() => {
    this.menusLocal.set(this.menus() ?? []);
  });

  showTooltip = computed<TooltipVisibility>(() => {
    const host = this.host();
    if (host === SECONDARY_NAVIGATION_HOSTS.BURGER) return 'hidden';

    return this.collapsed() ? 'visible' : 'auto';
  });

  getItemIcon(item: MenuItemType): Icon {
    return item.icon || icons.FOLDER;
  }

  getCurrentOrganizationWithInlineRoles(
    organizations: BadgeItemType[],
  ): Maybe<Organization & { roles: string }> {
    const currentOrganization = organizations.find((i) => i.current) as Organization;
    if (!currentOrganization) return undefined;

    return {
      ...currentOrganization,
      roles: this.#coreClientUtilService.getUserRoles(currentOrganization),
    };
  }

  handleItemClick(itemClicked: MenuItemType): void {
    this.#searchService?.clearSearch();
    this.#actionService.handleAction(itemClicked, this.#destroyRef);
    this.menusLocal.update((menus) =>
      menus.map((section) => ({
        ...section,
        items: section.items?.map((item) => ({
          ...item,
          selected: item.key === itemClicked.key,
        })),
      })),
    );
  }
}
